// The app's side of the bridge to the consent layer's own bundle, for the
// events a page knows about and a click cannot express: an account created, a
// key issued, a recognition finished, a payment credited.
//
// The bundle is built and loaded separately from this app (`boot.ts`), and on a
// page rendered per request the framework can hydrate before that script has
// run. So an event raised before the bridge exists is held in a short list on
// the window, which the bundle takes over when measurement is allowed — and
// which is simply never read when it is not. Nothing is written to the device
// either way.
//
// What may ride on an event is decided on the bundle's side
// (`google-analytics.ts`, `sanitizeEvent`), not here: a caller cannot widen it.

import type { GaIdentity } from "./google-analytics.ts";

/** Events raised before the bundle ran, oldest first. Bounded. */
export const PENDING_LIMIT = 20;

export type EventParams = Readonly<Record<string, string | number>>;

interface Bridge {
  pageview(path: string): void;
  event?(name: string, params: EventParams): void;
  identify?(identity: GaIdentity): void;
}

interface PendingHolder {
  __dcAnalytics?: Bridge;
  __dcAnalyticsPending?: [string, EventParams][];
  __dcAnalyticsIdentity?: GaIdentity;
}

function host(): PendingHolder | null {
  return typeof window === "undefined" ? null : (window as unknown as PendingHolder);
}

/** Sends one event, or holds it until the consent layer is ready for it. */
export function trackEvent(name: string, params: EventParams = {}, target = host()): void {
  if (target === null) return;
  const bridge = target.__dcAnalytics;
  if (bridge?.event) {
    bridge.event(name, params);
    return;
  }
  const pending = target.__dcAnalyticsPending ?? [];
  if (pending.length < PENDING_LIMIT) pending.push([name, params]);
  target.__dcAnalyticsPending = pending;
}

/**
 * States who is signed in, as the analytics may know it: the account's
 * pseudonym and whether this is our own traffic. Kept on the window as well, so
 * a bundle that starts later still finds it.
 */
export function identifyForAnalytics(identity: GaIdentity, target = host()): void {
  if (target === null) return;
  target.__dcAnalyticsIdentity = identity;
  target.__dcAnalytics?.identify?.(identity);
}
